// Robustly extract a human-readable message + stack from anything that may end
// up in `e.reason` of unhandledrejection, in `err` of Vue.config.errorHandler,
// in `event.error` of window 'error', or thrown anywhere. Always returns
// strings so downstream String()/substring() are safe.
//
// Returns { message, stack, name }:
//   - message: never empty; for serializable plain objects (including `{}`)
//              returns the JSON form ("{}", '{"foo":"bar"}', ...). Only falls
//              back to "[object Object]" when JSON.stringify fails (circular
//              refs, BigInt, etc.) — that case is rare and there is no better
//              representation available.
//   - stack:   '' when not available, never "undefined"/"null"
//   - name:    Error.name when applicable (TypeError, RangeError, ...), ''
const MAX_JSON_BYTES = 2000
const EMPTY = { message: 'unknown', stack: '', name: '' }
const EMPTY_STRING = { message: '(empty)', stack: '', name: '' }

// Limites de truncamento de client_message/stack — vivem aqui (módulo
// compartilhado) para não duplicar os magic numbers entre observability.js e
// o early buffer do main.js.
export const MESSAGE_MAX_LENGTH = 1000
export const STACK_MAX_LENGTH = 2000

function asString(value) {
  return typeof value === 'string' ? value : ''
}

function fromError(err) {
  return {
    message: err.message || err.name || 'Error',
    stack: asString(err.stack),
    name: asString(err.name) || 'Error',
  }
}

function fromObjectWithMessage(obj) {
  return {
    message: obj.message,
    stack: asString(obj.stack),
    name: asString(obj.name),
  }
}

function fromObjectJSON(obj) {
  try {
    const json = JSON.stringify(obj)
    // Accept "{}" as a valid representation — it preserves the "this was an
    // empty object" semantics and beats the opaque "[object Object]".
    if (typeof json === 'string') {
      return { message: json.slice(0, MAX_JSON_BYTES), stack: '', name: '' }
    }
  } catch (_serializationError) {
    // Circular ref or non-serializable — caller falls back to type tag.
  }
  return null
}

function fromObject(obj) {
  if (obj instanceof Error) return fromError(obj)
  if (typeof obj.message === 'string' && obj.message) {
    return fromObjectWithMessage(obj)
  }
  const json = fromObjectJSON(obj)
  if (json) return json
  return { message: Object.prototype.toString.call(obj), stack: '', name: '' }
}

export function stringifyError(input) {
  if (input == null) return EMPTY
  if (typeof input === 'string') {
    // .trim() para que reason só-espaços (Promise.reject('   ')) também caia no
    // sentinel em vez de virar um client_message em branco no Loki.
    return input.trim() ? { message: input, stack: '', name: '' } : EMPTY_STRING
  }
  if (typeof input === 'object') return fromObject(input)
  return { message: String(input), stack: '', name: '' }
}

// Prepend Error name to the message so Loki client_message stays useful
// ("TypeError: foo is not a function") without needing a separate field.
// Skip the prefix when message is empty, when name is empty, when message
// already begins with "<name>:", or when message equals name verbatim
// (happens with `new Error('')` where fromError uses name as the message
// fallback — prepending would yield "Error: Error").
export function formatClientMessage({ message, name }) {
  if (!message) return ''
  if (!name) return message
  if (message === name) return message
  if (message.startsWith(`${name}:`)) return message
  return `${name}: ${message}`
}

// Single entry point shared by captureError (plugin) and captureEarlyError
// (main.js bootstrap buffer). Accepts either a raw error/reason or a
// pre-extracted string; a BLANK string falls through to stringifyError so the
// '(empty)' sentinel applies — `Promise.reject('')` used to arrive at Loki
// as client_message="" (92% dos eventos error de prod em 2026-07-13).
export function extractErrorParts(input, stack) {
  const isUsableString =
    typeof input === 'string' && input.trim() && input !== '[object Object]'
  const extracted = isUsableString
    ? { message: input, stack: asString(stack), name: '' }
    : stringifyError(input)
  const finalStack =
    typeof stack === 'string' && stack ? stack : extracted.stack
  return {
    message: formatClientMessage(extracted),
    stack: String(finalStack || ''),
    name: extracted.name || '',
  }
}

const HTTP_STATUS_MIN = 100
const HTTP_STATUS_MAX = 599

function asValidHttpStatus(value) {
  const isValid =
    typeof value === 'number' &&
    value >= HTTP_STATUS_MIN &&
    value <= HTTP_STATUS_MAX
  return isValid ? value : null
}

// Pulls HTTP request metadata out of axios-style errors (err.response.status,
// err.config.url) and fetch Response-like objects (status, url). Returns
// httpStatus=null / requestUrl='' when the input carries no request info so
// callers can omit the fields from the telemetry payload entirely.
export function extractRequestInfo(input) {
  if (!input || typeof input !== 'object') {
    return { httpStatus: null, requestUrl: '' }
  }
  const response = input.response
  const config = input.config
  const httpStatus =
    asValidHttpStatus(response && response.status) ||
    asValidHttpStatus(input.status)
  const requestUrl =
    asString(config && config.url) ||
    asString(response && response.config && response.config.url) ||
    asString(input.url)
  return { httpStatus, requestUrl }
}
